import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { PageHeader } from "@/components/Layout";
import { api } from "@/lib/api";
import {
    Sprout,
    GraduationCap,
    Utensils,
    HandHeart,
    Trees,
    BookOpen,
    Soup,
    Stethoscope,
    Users,
    Droplets,
    Lightbulb,
    HeartHandshake,
    Hand,
    Brush,
} from "lucide-react";

const ICONS = {
    tree: Trees,
    broom: Brush,
    droplet: Droplets,
    graduation: GraduationCap,
    book: BookOpen,
    lightbulb: Lightbulb,
    utensils: Utensils,
    soup: Soup,
    "heart-handshake": HeartHandshake,
    stethoscope: Stethoscope,
    users: Users,
    "hand-heart": Hand,
    leaf: Sprout,
};

const CATEGORIES = [
    { key: "all", label: "All", icon: Sprout },
    { key: "environment", label: "Environment", icon: Sprout },
    { key: "education", label: "Education", icon: GraduationCap },
    { key: "food", label: "Food Relief", icon: Utensils },
    { key: "social", label: "Social Welfare", icon: HandHeart },
];

export default function Programs() {
    const [cat, setCat] = useState("all");
    const programsQ = useQuery({ queryKey: ["programs"], queryFn: () => api.get("/programs").then((r) => r.data) });
    const programs = (programsQ.data || []).filter((p) => cat === "all" || p.category === cat);

    return (
        <div data-testid="programs-page">
            <PageHeader
                eyebrow="Programs & Initiatives"
                title={<>Programs that put <span className="text-secondary italic font-light">people first</span>.</>}
                sub="From sapling drives to scholarship cheques, every program is designed with the community, for the community."
                image="https://images.pexels.com/photos/15119089/pexels-photo-15119089.jpeg?auto=compress&cs=tinysrgb&w=940"
            />

            <section className="mx-auto max-w-7xl px-5 lg:px-10 py-12">
                <div className="flex flex-wrap gap-2 mb-10">
                    {CATEGORIES.map((c) => (
                        <button
                            key={c.key}
                            data-testid={`programs-filter-${c.key}`}
                            onClick={() => setCat(c.key)}
                            className={`px-5 py-2.5 rounded-full text-sm border transition-all ${
                                cat === c.key
                                    ? "bg-primary text-primary-foreground border-primary"
                                    : "bg-background border-border hover:border-primary/40"
                            }`}
                        >
                            {c.label}
                        </button>
                    ))}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                    {programs.map((p, i) => {
                        const Icon = ICONS[p.icon] || Sprout;
                        return (
                            <div
                                key={p.id}
                                data-testid={`program-card-${p.id}`}
                                className="group bg-card border border-border hover:border-primary/40 hover:-translate-y-1 transition-all"
                            >
                                <div className="aspect-[16/10] overflow-hidden bg-muted">
                                    <img
                                        src={p.image_url}
                                        alt={p.title}
                                        loading="lazy"
                                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                                    />
                                </div>
                                <div className="p-6">
                                    <div className="flex items-center gap-3 mb-3">
                                        <div className="h-10 w-10 rounded-full bg-accent grid place-items-center">
                                            <Icon className="h-5 w-5 text-primary" />
                                        </div>
                                        <span className="font-eyebrow text-[10px] text-muted-foreground">{p.category}</span>
                                    </div>
                                    <h3 className="font-display text-xl">{p.title}</h3>
                                    <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{p.description}</p>
                                </div>
                            </div>
                        );
                    })}
                </div>

                {programsQ.isLoading && <div className="text-muted-foreground">Loading programs…</div>}
                {!programsQ.isLoading && programs.length === 0 && (
                    <div className="text-muted-foreground">No programs in this category yet.</div>
                )}
            </section>
        </div>
    );
}
